import api from './api';

// Question API service functions
export const questionService = {
  // Get all questions
  async getQuestions(params = {}) {
    try {
      const response = await api.get('/questions', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching questions:', error);
      throw error;
    }
  },

  // Get inactive questions
  async getInactiveQuestions() {
    try {
      const response = await api.get('/questions/inactive');
      return response.data;
    } catch (error) {
      console.error('Error fetching inactive questions:', error);
      throw error;
    }
  },

  // Get a specific question
  async getQuestion(id) {
    try {
      const response = await api.get(`/questions/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching question ${id}:`, error);
      throw error;
    }
  },

  // Create a new question
  async createQuestion(questionData) {
    try {
      const response = await api.post('/questions', questionData);
      return response.data;
    } catch (error) {
      console.error('Error creating question:', error);
      throw error;
    }
  },

  // Update a question
  async updateQuestion(id, questionData) {
    try {
      const response = await api.put(`/questions/${id}`, questionData);
      return response.data;
    } catch (error) {
      console.error('Error updating question:', error);
      throw error;
    }
  },

  // Delete a question
  async deleteQuestion(id) {
    try {
      const response = await api.delete(`/questions/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting question:', error);
      throw error;
    }
  },

  // Reactivate an inactive question
  async reactivateQuestion(id) {
    try {
      const response = await api.post(`/questions/${id}/reactivate`);
      return response.data;
    } catch (error) {
      console.error('Error reactivating question:', error);
      throw error;
    }
  },

  // Link question to protocol
  async linkQuestionToProtocol(questionId, protocolId) {
    try {
      const response = await api.post(`/questions/${questionId}/link`, {
        protocol_id: protocolId
      });
      return response.data;
    } catch (error) {
      console.error('Error linking question to protocol:', error);
      throw error;
    }
  },

  // Bulk link questions to a protocol (accept ordered question ids)
  async bulkLinkQuestions(protocolId, questionIds = []) {
    if (!protocolId) throw new Error('protocolId is required');
    if (!Array.isArray(questionIds)) throw new Error('questionIds must be an array');
    try {
      // send multiple variations of the ordering information so backend handlers
      // using different keys will accept the ordering payload
      const numericIds = questionIds.map(id => Number(id));
      const body = {
        protocol_id: Number(protocolId),
        question_ids: numericIds,
        // some backends expect ordered list under this key
        ordered_question_ids: numericIds,
        // some backends expect an array of {question_id, order}
        question_orders: numericIds.map((qid, idx) => ({ question_id: qid, order: idx + 1 })),
        // legacy single/plural
        question_id: numericIds[0] ?? null
      };

      const response = await api.post('/questions/bulk-link', body, {
        headers: { 'Content-Type': 'application/json' }
      });
      return response.data;
    } catch (error) {
      console.error('Error bulk linking questions:', error);
      throw error;
    }
  },

  // Unlink question from protocol (legacy - uses multi-protocol endpoint)
  async unlinkQuestionFromProtocol(questionId, protocolId) {
    if (!questionId) throw new Error('questionId is required');
    if (!protocolId) throw new Error('protocolId is required');

    try {
      const response = await api.post(
        `/questions/${questionId}/unlink`,
        {
          protocol_ids: [Number(protocolId)]
        },
        {
          headers: { 'Content-Type': 'application/json' }
        }
      );

      // Check for specific error conditions
      if (response.data.error) {
        throw new Error(response.data.error);
      }

      if (response.data.blocked_protocols?.includes(protocolId)) {
        throw new Error('Cannot unlink - question has answers in this protocol');
      }

      return response.data;
    } catch (error) {
      console.error('Error unlinking question:', error);
      throw error;
    }
  },

  // NEW: Dedicated single question unlink with detailed logging
  async unlinkSingleQuestionFromProtocol(questionId, protocolId) {
    if (!questionId) throw new Error('questionId is required');
    if (!protocolId) throw new Error('protocolId is required');

    try {
      console.log(`Unlinking question ${questionId} from protocol ${protocolId}`);
      const response = await api.post(
        `/questions/${questionId}/unlink-single`,
        {
          protocol_id: Number(protocolId)
        },
        {
          headers: { 'Content-Type': 'application/json' }
        }
      );

      console.log('Unlink response:', response.data);

      // Check for specific error conditions
      if (response.data.error) {
        throw new Error(response.data.error);
      }

      return response.data;
    } catch (error) {
      console.error('Error unlinking single question:', error);
      throw error;
    }
  },

  // Get question statistics
  async getQuestionStats() {
    try {
      const response = await api.get('/questions/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching question stats:', error);
      throw error;
    }
  },

  // Bulk unlink questions from protocol (accept protocolId)
  async bulkUnlinkQuestions(questionIds, protocolId) {
    if (!Array.isArray(questionIds) || questionIds.length === 0) throw new Error('questionIds required');
    if (!protocolId) throw new Error('protocolId is required');
    try {
      // include both singular and plural keys to satisfy either backend implementation
      const body = {
        question_ids: questionIds,
        protocol_id: Number(protocolId),
        protocol_ids: [Number(protocolId)]
      };
      const response = await api.post('/questions/bulk-unlink', body, {
        headers: { 'Content-Type': 'application/json' }
      });
      return response.data;
    } catch (error) {
      console.error('Error bulk unlinking questions:', error);
      throw error;
    }
  },

  // NEW: Append-only bulk link questions to a protocol
  async bulkLinkQuestionsAppendOnly(protocolId, questionIds = []) {
    if (!protocolId) throw new Error('protocolId is required');
    if (!Array.isArray(questionIds)) throw new Error('questionIds must be an array');
    try {
      console.log(`Append-only linking ${questionIds.length} questions to protocol ${protocolId}`);
      const numericIds = questionIds.map(id => Number(id));
      const body = {
        protocol_id: Number(protocolId),
        question_ids: numericIds
      };

      const response = await api.post('/questions/bulk-link-append', body, {
        headers: { 'Content-Type': 'application/json' }
      });
      
      console.log('Append-only link response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error append-only linking questions:', error);
      throw error;
    }
  }
};

export const getQuestion = (id) => api.get(`/questions/${id}`);
export const updateQuestion = (id, data) => api.put(`/questions/${id}`, data);
export const deleteQuestion = (id) => api.delete(`/questions/${id}`);
export const forceDeleteQuestion = (id) => api.delete(`/questions/${id}/force-delete`);
export const rawDeleteQuestion = (id) => api.delete(`/questions/${id}?raw=true`);

// add/modify unlinkQuestionFromProtocol API call
export const unlinkQuestionFromProtocol = async (questionId, protocolId) => {
  if (!questionId) throw new Error('questionId is required');
  if (!protocolId) throw new Error('protocolId is required');
  // backend expects protocol_ids array
  const resp = await api.post(
    `/questions/${questionId}/unlink`,
    { protocol_ids: [Number(protocolId)] },
    { headers: { 'Content-Type': 'application/json' } }
  );
  return resp.data;
};